import React, { useState, useEffect, useRef } from 'react'
import { Formik, Form } from 'formik'
import styled from 'styled-components'

import { Link, useSearchParams, useNavigate, useParams } from 'react-router-dom'

import { toast } from 'react-toastify'
import dayjs from 'dayjs'
import hexToRgba from 'hex-to-rgba'

import { useVideos } from '../../VideoProvider'
import { useCampaign } from './hooks/useCampaign'
import { useModal } from '../../Modal'
import { useAuth } from '../../Auth'

import api from '../../api.js'
import { VIDEO_OBJECT_TYPE, WIDGET_TYPES } from '../types'
import { processVideoObjects, createVideoObjects, createSelectedVideos, getWidgetUrlRules } from './utils/videoHelpers'
import { isFreePlan } from '../../Plans'

import { Flex } from '../../ui/Layout'
import Icon from '../../ui/Icon'
import { Text } from '../../ui/Typography'
import Button from '../../ui/Button'
import Spinner, { BigDarkSpinner } from '../../ui/Spinner'

import WidgetPreview from '../../Components/WidgetPreview'
import WidgetNameModal from './Components/WidgetNameModal'
import WidgetOptions from '../Create/WidgetOptions'
import Modals from './Components/Modals'
import FullScreenSettings from './Components/FullScreenSettings'
import CarouselPreview from '../../Components/CarouselPreview'
import { BrandingUploader } from '../../Account/index'
import TitleSettings from './Components/TitleSettings'
import { checkIfModalSeen, markModalAsSeen } from './Components/HelpModal'

const LeftBarIconContainer = styled(Flex)`
  flex-direction: column;
  width: 58px;
  height: 58px;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  margin-bottom: 16px;
  background-color: ${(props) => (props.isActive ? '#e2e8f0' : 'transparent')};
  &:hover {
    background-color: #e2e8f0;
  }
`

const LeftBarIcon = ({ icon, text, onClick, isActive }) => {
  return (
    <LeftBarIconContainer isActive={isActive} onClick={onClick}>
      <Icon mb="8px" icon={icon} width={16} height={17} />
      <Text light fontSize="11px" fontWeight="500" lineHeight="normal">
        {text}
      </Text>
    </LeftBarIconContainer>
  )
}

const EDITOR_CONTENT = {
  WIDGET_OPTIONS: 'WIDGET_OPTIONS',
  FULL_SCREEN_SETTINGS: 'FULL_SCREEN_SETTINGS',
  BRAND_SETTINGS: 'BRAND_SETTINGS',
  TITLE_SETTINGS: 'TITLE_SETTINGS'
}

export const MODAL_OPEN_TYPES = {
  BUTTONS: 'BUTTONS',
  TITLES: 'TITLES',
  SHOPPING: 'SHOPPING',
  NAVIGATION: 'NAVIGATION'
}

const CampaignEditor = () => {
  let { user } = useAuth()
  const { videos, images, refresh } = useVideos()
  const { campaignId } = useParams()
  const navigate = useNavigate()
  const [search, setSearch] = useSearchParams()
  const { campaign, widgets, isLoading } = useCampaign(campaignId)

  const { setShowInstallEmbedModal, setShowPaywallModal } = useModal()
  const [showCampaignNameModal, setShowCampaignNameModal] = useState(false)
  const [showWidgetNameModal, setShowWidgetNameModal] = useState(false)
  const [showShareModal, setShowShareModal] = useState(false)
  const [showUltimateInstallModal, setShowUltimateInstallModalState] = useState(false)
  const [modalOpenType, setModalOpenType] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showMobilePreview, setShowMobilePreview] = useState(false)
  const [showHelpModal, setShowHelpModal] = useState(false)
  const [promotedTagsOpen, setPromotedTagsOpen] = useState(false)
  const [filterTagsOpen, setFilterTagsOpen] = useState(false)

  const setShowUltimateInstallModal = (bool) => {
    // if (bool && isFreePlan(user)) {
    //   setShowPaywallModal(true)
    //   return
    // }
    setShowUltimateInstallModalState(bool)
  }

  useEffect(() => {
    if (search.get('showHelpModal')) {
      if (!checkIfModalSeen()) {
        setShowHelpModal(true)
        markModalAsSeen()
      }
      const newSearch = new URLSearchParams(search)
      newSearch.delete('showHelpModal')
      setSearch(newSearch)
    }
  }, [search])

  const [isMobile, setIsMobile] = useState(false)

  const { buttonLinks, buttonTexts, titles, subtitles } = processVideoObjects(campaign)
  const { widgetRuleType0, widgetRuleUrl0, matchType } = getWidgetUrlRules(campaign)
  if (isLoading || !campaign || !campaign.id) {
    return (
      <Flex justifyContent="center" alignItems="center" height="100vh">
        <BigDarkSpinner />
      </Flex>
    )
  }

  return (
    <Formik
      initialValues={{
        ...campaign,
        widget: campaign && campaign.widget ? campaign.widget : {},
        buttonLinks,
        buttonTexts,
        titles,
        subtitles,
        widgetRuleType0,
        widgetRuleUrl0,
        matchType
      }}
    >
      {({ values, setFieldValue }) => {
        const activeWidget = widgets && values.widgetId && widgets.find((w) => w.id === values.widgetId)
        const videoObjects = createVideoObjects(values)
        const selectedVideos = createSelectedVideos(values, videos, images)

        const [editorContent, setEditorContent] = useState(
          values.isShareCampaign || values.isCustomLauncher ? EDITOR_CONTENT.FULL_SCREEN_SETTINGS : EDITOR_CONTENT.WIDGET_OPTIONS
        )

        const [isSubmitting, setIsSubmitting] = useState(false)
        const submit = async () => {
          if (isSubmitting) {
            return
          }
          try {
            setIsSubmitting(true)
            if (!values.widget.name) {
              values.widget.name = `Widget - ${widgets.length + 1}`
            }
            const createWidgetsRes = await api.post(`/campaign/widget/create`, values.widget)
            setFieldValue('widgetId', createWidgetsRes.data.payload.id)
            values.widgetId = createWidgetsRes.data.payload.id
            if (values.buttonOpacity !== undefined) {
              setFieldValue('buttonRgba', hexToRgba(values.buttonColor, values.buttonOpacity / 100))
              values.buttonRgba = hexToRgba(values.buttonColor, values.buttonOpacity / 100)
            }

            const res = await api.post('/campaign/update', values)

            if (search.get('showInstallModal')) {
              setShowUltimateInstallModal(true)
              setSearch({})
            }
            if (search.get('showShareModal')) {
              setShowShareModal(true)
              setSearch({})
            }
          } catch (e) {
            console.log(e)
          } finally {
            setIsSubmitting(false)
          }
        }

        return (
          <Flex bg="white" flexDirection="column">
            {/* HEADER START */}
            <Flex
              cursor="pointer"
              width="auto"
              alignItems="center"
              justifyContent="center"
              height="54px"
              borderBottom="1px solid #EDF2F7;"
            >
              <Flex
                onClick={() => {
                  // submit(undefined, true)
                  navigate('/campaigns/edit/' + campaignId)
                }}
                width="68px"
                minWidth="68px"
                height="100%"
                alignItems="center"
                justifyContent="center"
              >
                <Icon icon="chevron-left-light" height="16px" width="16px" />
              </Flex>

              <Flex height="100%" alignItems="center" justifyContent="flex-end" pr="24px">
                <Flex pl="24px">
                  <Flex
                    width="auto"
                    mr="8px"
                    alignItems="center"
                    onClick={() => {
                      setShowCampaignNameModal(true)
                    }}
                    cursor="pointer"
                    borderRight="1.9px solid #425466"
                  >
                    <Text light fontSize="16px" fontWeight="500" mr="8px" maxWidth="300px">
                      {values.name || 'Add campaign name'}
                    </Text>
                  </Flex>
                  {values.widget.name ? (
                    <Flex
                      width="auto"
                      alignItems="center"
                      onClick={() => setShowWidgetNameModal(true)}
                      cursor="pointer"
                      mr="24px"
                    >
                      <Text lightLight fontSize="16px" fontWeight="500" mr="16px">
                        {values.widget.name}
                      </Text>
                    </Flex>
                  ) : null}
                </Flex>

                {editorContent === EDITOR_CONTENT.FULL_SCREEN_SETTINGS ? (
                  <>
                    <Text fontWeight="500" mr="16px" whiteSpace="nowrap">
                      Player Preview
                    </Text>
                    <Button
                      renderCenterIcon={() => <Icon width={12} height={12} icon="desktop-black" />}
                      variant="white"
                      shadow
                      onClick={() => {
                        setTimeout(() => window.open(`https://getclipara.com/share/${values.id}`), 300)
                      }}
                      zIndex={1}
                      width="52px"
                      minWidth="52px"
                      mr="8px"
                    />
                    <Button
                      renderCenterIcon={() => <Icon width={12} height={12} icon="phone-black" />}
                      variant="white"
                      shadow
                      onClick={() => setShowMobilePreview(true)}
                      zIndex={1}
                      width="52px"
                      minWidth="52px"
                      mr="16px"
                    />
                  </>
                ) : null}

                {values.widget &&
                values.widget.type !== WIDGET_TYPES.FLOATING &&
                values.widget.type !== WIDGET_TYPES.SINGLE_EMBED &&
                values.widget.type !== WIDGET_TYPES.INLINE_SINGLE &&
                editorContent !== EDITOR_CONTENT.FULL_SCREEN_SETTINGS ? (
                  <>
                    <Button
                      renderCenterIcon={() => <Icon width={12} height={12} icon="desktop-black" />}
                      variant={isMobile ? 'grey' : 'white'}
                      shadow
                      onClick={() => {
                        setIsMobile(false)
                      }}
                      zIndex={1}
                      width="52px"
                      minWidth="52px"
                      mr="8px"
                    />
                    <Button
                      renderCenterIcon={() => <Icon width={12} height={12} icon="phone-black" />}
                      variant={!isMobile ? 'grey' : 'white'}
                      shadow
                      onClick={() => {
                        setIsMobile(true)
                      }}
                      zIndex={1}
                      width="52px"
                      minWidth="52px"
                      mr="16px"
                    />
                  </>
                ) : null}
                {values.isShareCampaign ? null : (
                  <Button
                    mr="16px"
                    onClick={() => {
                      submit()
                      setShowUltimateInstallModal(true)
                    }}
                    variant="grey"
                    label="Install"
                    zIndex={1}
                  />
                )}
                {values.isShareCampaign ? (
                  <Button
                    mr="16px"
                    renderLeftIcon={() => <Icon width={12} height={12} icon="share-black" />}
                    variant="grey"
                    label="Share"
                    onClick={() => {
                      setShowShareModal(true)
                    }}
                  />
                ) : null}
                <Button
                  zIndex={2}
                  isLoading={isSubmitting}
                  onClick={async () => {
                    await submit(undefined, true)
                    if (values.showInstallModal) {
                      setShowUltimateInstallModal(true)
                      setFieldValue('showInstallModal', false)
                    }
                    toast('Campaign saved')
                  }}
                  variant="black"
                  label="Save"
                  mr="24px"
                />
                <Icon icon="chat-question-grey" width={20} height={20} cursor="pointer" onClick={() => setShowHelpModal(true)} />
              </Flex>
            </Flex>
            {/* HEADER END */}
            <Flex>
              <Flex
                flexDirection="column"
                minHeight="calc(100vh - 54px)"
                maxHeight="calc(100vh - 54px)"
                height="calc(100vh - 54px)"
                width={values.widget?.type === WIDGET_TYPES.SEARCH ? '0px' : '68px'}
                minWidth={values.widget?.type === WIDGET_TYPES.SEARCH ? '0px' : '68px'}
                overflowY="scroll"
                borderRight="1px solid #EDF2F7;"
                alignItems="center"
                pt="16px"
              >
                {values.widget?.type === WIDGET_TYPES.SEARCH ? null : (
                  <LeftBarIcon
                    icon="content-dark-grey"
                    text="Content"
                    onClick={async () => {
                      await submit()
                      navigate(`/campaigns/edit/${campaignId}?step=SELECT_VIDEO&backPage=/campaigns/editor/${campaignId}`)
                    }}
                  />
                )}
                {values.isShareCampaign || values.isCustomLauncher ? null : (
                  <LeftBarIcon
                    icon="design-dark-grey"
                    text="Design"
                    isActive={editorContent === EDITOR_CONTENT.WIDGET_OPTIONS}
                    onClick={() => setEditorContent(EDITOR_CONTENT.WIDGET_OPTIONS)}
                  />
                )}
                {values.widget?.type === WIDGET_TYPES.SEARCH ||
                values.widget?.type === WIDGET_TYPES.FLOATING ||
                values.widget?.type === WIDGET_TYPES.INLINE_SINGLE ? null : (
                  <LeftBarIcon
                    icon="titles-dark-grey"
                    text="Titles"
                    isActive={editorContent === EDITOR_CONTENT.TITLE_SETTINGS}
                    onClick={() => setEditorContent(EDITOR_CONTENT.TITLE_SETTINGS)}
                  />
                )}
                {values.widget?.type === WIDGET_TYPES.SEARCH ||
                values.widget?.type === WIDGET_TYPES.NAVIGATION_CAROUSEL ||
                values.widget?.type === WIDGET_TYPES.NAVIGATION_GRID ||
                values.widget?.type === WIDGET_TYPES.INLINE_CAROUSEL ||
                values.widget?.type === WIDGET_TYPES.INLINE_SINGLE ||
                values.isShoppable ? null : (
                  <LeftBarIcon
                    icon="actions-dark-grey"
                    text="Actions"
                    onClick={() => setModalOpenType(MODAL_OPEN_TYPES.BUTTONS)}
                  />
                )}
                {/* NAVIGATION SPECIFIC ACTIONS */}
                {values.widget?.type === WIDGET_TYPES.NAVIGATION_CAROUSEL ||
                values.widget?.type === WIDGET_TYPES.NAVIGATION_GRID ||
                values.isShoppable ? (
                  <LeftBarIcon
                    icon="actions-dark-grey"
                    text="Actions"
                    onClick={() => setModalOpenType(MODAL_OPEN_TYPES.NAVIGATION)}
                  />
                ) : null}
                {values.isShoppable ? (
                  <LeftBarIcon
                    icon="shopping-dark-grey"
                    text="Shopping"
                    onClick={() => setModalOpenType(MODAL_OPEN_TYPES.SHOPPING)}
                  />
                ) : null}
                {values.widget?.type === WIDGET_TYPES.SEARCH ||
                values.widget?.type === WIDGET_TYPES.NAVIGATION_CAROUSEL ||
                values.widget?.type === WIDGET_TYPES.NAVIGATION_GRID ||
                values.widget?.type === WIDGET_TYPES.INLINE_CAROUSEL ||
                values.widget?.type === WIDGET_TYPES.INLINE_SINGLE ? null : (
                  <LeftBarIcon
                    icon="brand-dark-grey"
                    text="Brand"
                    isActive={editorContent === EDITOR_CONTENT.BRAND_SETTINGS}
                    onClick={() => setEditorContent(EDITOR_CONTENT.BRAND_SETTINGS)}
                  />
                )}
                {values.widget?.type === WIDGET_TYPES.SEARCH ||
                values.widget?.type === WIDGET_TYPES.INLINE_SINGLE ||
                values.widget?.type === WIDGET_TYPES.INLINE_CAROUSEL ||
                values.widget?.type === WIDGET_TYPES.NAVIGATION_CAROUSEL ||
                values.widget?.type === WIDGET_TYPES.NAVIGATION_GRID ? null : (
                  <LeftBarIcon
                    icon="settings-dark-grey"
                    text="Player"
                    isActive={editorContent === EDITOR_CONTENT.FULL_SCREEN_SETTINGS}
                    onClick={() => setEditorContent(EDITOR_CONTENT.FULL_SCREEN_SETTINGS)}
                  />
                )}
              </Flex>
              <Flex
                flexDirection="column"
                borderRight="1px solid #EDF2F7;"
                minHeight="calc(100vh - 54px)"
                maxHeight="calc(100vh - 54px)"
                height="calc(100% - 54px)"
                width="400px"
                minWidth="400px"
                overflowY="scroll"
              >
                {editorContent === EDITOR_CONTENT.WIDGET_OPTIONS ? (
                  <Flex flexDirection="column" pb="40px">
                    <WidgetOptions
                      isSubmitting={isSubmitting}
                      isMobile={isMobile}
                      setIsMobile={setIsMobile}
                      widgets={widgets}
                      selectedVideos={selectedVideos}
                      setFieldValue={setFieldValue}
                      values={values}
                      widget={values.widget}
                      videos={videos}
                      images={images}
                      refresh={refresh}
                      setModalOpenType={setModalOpenType}
                      promotedTagsOpen={promotedTagsOpen}
                      setPromotedTagsOpen={setPromotedTagsOpen}
                      filterTagsOpen={filterTagsOpen}
                      setFilterTagsOpen={setFilterTagsOpen}
                    />
                  </Flex>
                ) : null}
                {editorContent === EDITOR_CONTENT.FULL_SCREEN_SETTINGS ? (
                  <FullScreenSettings values={values} setFieldValue={setFieldValue} selectedVideos={selectedVideos} />
                ) : null}
                {editorContent === EDITOR_CONTENT.BRAND_SETTINGS ? (
                  <Flex p="24px" flexDirection="column">
                    <BrandingUploader />
                  </Flex>
                ) : null}
                {editorContent === EDITOR_CONTENT.TITLE_SETTINGS ? (
                  <Flex flexDirection="column" pb="60px">
                    <TitleSettings
                      values={values}
                      setFieldValue={setFieldValue}
                      widget={values.widget}
                      isMobile={isMobile}
                      setIsMobile={setIsMobile}
                      setModalOpenType={setModalOpenType}
                    />
                  </Flex>
                ) : null}
              </Flex>
              <Flex
                alignItems="center"
                flexDirection="column"
                position="relative"
                width="calc(100vw - 400px);"
                height="calc(100vh - 54px)"
                overflow="hidden"
              >
                {(() => {
                  let showWidgetPreview = !values.isShareCampaign && values.widgetId
                  console.log({ showWidgetPreview, isShareCampaign: values.isShareCampaign, widgetId: values.widgetId })
                  if (editorContent === EDITOR_CONTENT.FULL_SCREEN_SETTINGS) {
                    showWidgetPreview = false
                  }
                  if (!showWidgetPreview) {
                    return (
                      <CarouselPreview
                        values={values}
                        setFieldValue={setFieldValue}
                        widget={activeWidget}
                        py="60px"
                        isLight
                        campaign={{ ...values, videoObjects }}
                        videos={selectedVideos || []}
                        noModalInteraction
                      />
                    )
                  }
                  return (
                    <WidgetPreview
                      isMobile={isMobile}
                      setIsMobile={setIsMobile}
                      widget={values.widget}
                      videos={campaign?.videos}
                      campaign={{ ...values, videoObjects }}
                    />
                  )
                })()}
                <Modals
                  setIsSubmitting={setIsSubmitting}
                  submit={submit}
                  showCampaignNameModal={showCampaignNameModal}
                  setShowCampaignNameModal={setShowCampaignNameModal}
                  showWidgetNameModal={showWidgetNameModal}
                  setShowWidgetNameModal={setShowWidgetNameModal}
                  showUltimateInstallModal={showUltimateInstallModal}
                  setShowUltimateInstallModal={setShowUltimateInstallModal}
                  showMobilePreview={showMobilePreview}
                  setShowMobilePreview={setShowMobilePreview}
                  values={values}
                  selectedVideos={selectedVideos}
                  videos={videos}
                  setFieldValue={setFieldValue}
                  activeWidget={activeWidget}
                  modalOpenType={modalOpenType}
                  setModalOpenType={setModalOpenType}
                  showShareModal={showShareModal}
                  setShowShareModal={setShowShareModal}
                  showHelpModal={showHelpModal}
                  setShowHelpModal={setShowHelpModal}
                />
              </Flex>
            </Flex>
          </Flex>
        )
      }}
    </Formik>
  )
}

export default CampaignEditor
